<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAgentsList } from 'dashboard/composables/useAgentsList';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  conversation: { type: Object, required: true },
});
const emit = defineEmits(['close']);
const store = useStore();
const { t } = useI18n();
const { agentsList } = useAgentsList(false);
const dialog = ref(null);
const search = ref('');
const selectedId = ref(null);
const isLoading = ref(true);
const isTransferring = ref(false);
const loadError = ref(false);
const transferError = ref(false);
const currentUser = computed(() => store.getters.getCurrentUser);
const availabilityLabels = computed(() => ({
  online: t('CONVERSATION.TRANSFER.STATUS.online'),
  busy: t('CONVERSATION.TRANSFER.STATUS.busy'),
  offline: t('CONVERSATION.TRANSFER.STATUS.offline'),
}));

const eligibleAgents = computed(() => {
  const { assignee, assignee_type: assigneeType } = props.conversation.meta;
  return agentsList.value.filter(
    agent =>
      (agent.assignee_type || 'User') === 'User' &&
      agent.id !== currentUser.value.id &&
      !((assigneeType || 'User') === 'User' && agent.id === assignee?.id)
  );
});
const filteredAgents = computed(() => {
  const query = search.value.trim().toLowerCase();
  return eligibleAgents.value.filter(agent =>
    `${agent.name} ${agent.email}`.toLowerCase().includes(query)
  );
});
const selectedAgent = computed(() =>
  eligibleAgents.value.find(agent => agent.id === selectedId.value)
);

onMounted(async () => {
  dialog.value.open();
  try {
    await store.dispatch('inboxAssignableAgents/fetch', [
      props.conversation.inbox_id,
    ]);
  } catch (error) {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
});

const transfer = async () => {
  if (
    !selectedAgent.value ||
    isLoading.value ||
    loadError.value ||
    isTransferring.value
  )
    return;

  const agent = selectedAgent.value;
  const conversationId = props.conversation.id;
  isTransferring.value = true;
  transferError.value = false;
  try {
    const assignee = await store.dispatch('assignAgent', {
      conversationId,
      agentId: agent.id,
      assigneeType: 'User',
    });
    // The shared action returns null when the assignment request fails.
    if (!assignee || assignee.id !== agent.id) {
      transferError.value = true;
      return;
    }
    useAlert(t('CONVERSATION.TRANSFER.SUCCESS', { name: agent.name }));
    emit('close');
  } catch (error) {
    transferError.value = true;
  } finally {
    isTransferring.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialog"
    :title="t('CONVERSATION.TRANSFER.TITLE')"
    :description="t('CONVERSATION.TRANSFER.DESCRIPTION')"
    :confirm-button-label="t('CONVERSATION.TRANSFER.ACTION')"
    :disable-confirm-button="!selectedAgent || isLoading || loadError"
    :is-loading="isTransferring"
    width="md"
    @confirm="transfer"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-3">
      <input
        v-model="search"
        type="search"
        :placeholder="t('CONVERSATION.TRANSFER.SEARCH')"
        :aria-label="t('CONVERSATION.TRANSFER.SEARCH')"
        :disabled="isLoading || loadError || isTransferring"
        class="!mb-0"
      />
      <p v-if="isLoading" class="m-0 py-5 text-sm text-n-slate-11">
        {{ t('CONVERSATION.TRANSFER.LOADING') }}
      </p>
      <p v-else-if="loadError" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ t('CONVERSATION.TRANSFER.LOAD_ERROR') }}
      </p>
      <fieldset
        v-else-if="filteredAgents.length"
        :disabled="isTransferring"
        class="flex flex-col gap-1 max-h-64 overflow-y-auto m-0 p-0 border-0"
        :aria-label="t('CONVERSATION.TRANSFER.TITLE')"
      >
        <label
          v-for="agent in filteredAgents"
          :key="agent.id"
          class="flex items-center gap-3 p-3 m-0 rounded-lg cursor-pointer hover:bg-n-alpha-2"
          :class="{ 'bg-n-alpha-2': selectedId === agent.id }"
        >
          <input
            v-model="selectedId"
            type="radio"
            name="transfer-agent"
            :value="agent.id"
            class="!m-0"
          />
          <Avatar
            :name="agent.name"
            :src="agent.thumbnail"
            :status="agent.availability_status"
            :size="32"
          />
          <span class="flex-1 min-w-0 truncate text-sm text-n-slate-12">
            {{ agent.name }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ availabilityLabels[agent.availability_status] }}
          </span>
        </label>
      </fieldset>
      <p v-else class="m-0 py-5 text-sm text-n-slate-11">
        {{
          eligibleAgents.length
            ? t('CONVERSATION.TRANSFER.NO_RESULTS')
            : t('CONVERSATION.TRANSFER.EMPTY')
        }}
      </p>
      <p v-if="transferError" role="alert" class="m-0 text-sm text-n-ruby-11">
        {{ t('CONVERSATION.TRANSFER.ERROR') }}
      </p>
    </div>
  </Dialog>
</template>
